import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@wit-support/ui';
import { isRequestLogDisabled, listRequestLog, requestIdOf } from '../api.js';
import { ADS_QUERY_DEFAULTS, REFERENCE_STALE_MS } from '../query-policy.js';
import { describeAdsError } from '../shared.js';
/**
 * Ключ признака «журнал включён» — пункт меню «Журнал» (SB-6) и кнопка тоста.
 * Вне префикса `['ads', …]`: инвалидация данных раздела проверку не дёргает.
 */
export const JOURNAL_AVAILABLE_KEY = ['fb-launches-journal-available'];
/** Ключ «журнал отвечает» (последняя проверка — 2xx) — только для кнопки тоста. */
const JOURNAL_REACHABLE_KEY = ['fb-launches-journal-reachable'];
/** Лёгкая проверка: окно в прошлом даёт пустую страницу, сервер не подбирает названия «Что». */
const probe = () => listRequestLog({ to: '1970-01-01T00:00:00.000Z', limit: 1 });
/**
 * Журнал включён? Только 404 `ads_request_log_disabled` значит «выключен»;
 * прочие сбои (сеть, 500) пункт меню не прячут — экран сам покажет «Повторить».
 */
export async function journalAvailable() {
    try {
        await probe();
        return true;
    }
    catch (err) {
        return !isRequestLogDisabled(err);
    }
}
/** Журнал отвечает: проверка вернула 2xx (выключен, нет прав, сбой — нет). */
async function journalReachable() {
    try {
        await probe();
        return true;
    }
    catch {
        return false;
    }
}
export const JOURNAL_PATH = '/fb-launches/journal';
export const JOURNAL_ACTION_LABEL = 'Подробнее в журнале';
/**
 * Кнопка [Подробнее в журнале] видна, только когда журнал включён (признак
 * пункта меню SB-6) И последняя проверка ответила 2xx — кнопка не ведёт на
 * экран, который не откроется. Пока неизвестно — нет.
 */
export function useJournalAvailable() {
    const enabled = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: JOURNAL_AVAILABLE_KEY,
        queryFn: journalAvailable,
        staleTime: REFERENCE_STALE_MS,
    });
    const reachable = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: JOURNAL_REACHABLE_KEY,
        queryFn: journalReachable,
        staleTime: REFERENCE_STALE_MS,
        enabled: enabled.data === true,
    });
    return enabled.data === true && reachable.data === true;
}
/**
 * Действие тоста [Подробнее в журнале] для ошибки вызова: журнал с «Кто: все»
 * и раскрытой строкой этой попытки (`?requestId=`). Нет номера попытки или
 * журнал выключен — null.
 */
export function useJournalAction() {
    const available = useJournalAvailable();
    const navigate = useNavigate();
    return useCallback((err) => {
        const id = requestIdOf(err);
        if (!available || id == null)
            return null;
        return {
            label: JOURNAL_ACTION_LABEL,
            onClick: () => void navigate({ to: JOURNAL_PATH, search: { requestId: id } }),
        };
    }, [available, navigate]);
}
/**
 * Тост ошибки экранов пачки и запуска: текст ошибки и, если есть, своя кнопка
 * (`action` — «Открыть пачку», «Поставить на паузу») и [Подробнее в журнале].
 * Две кнопки — журнал вторым (`cancel`).
 */
export function useAdsErrorToast() {
    const journal = useJournalAction();
    return useCallback((err, opts = {}) => {
        const text = opts.text ?? describeAdsError(err, 'write');
        const j = journal(err);
        const own = opts.action ?? null;
        if (own && j)
            toast.error(text, { action: own, cancel: j });
        else if (own ?? j)
            toast.error(text, { action: (own ?? j) });
        else
            toast.error(text);
    }, [journal]);
}
//# sourceMappingURL=journal-link.js.map